import Reveal from "@/components/Reveal";
import CountUp from "@/components/CountUp";

const PILLARS = [
  {
    tag: "Built, Owned, Defensible",
    title: "We don't rent — we own.",
    body: "TechBytes.org.uk + LeadAudit AI — flagship properties that turn intent signals into pipeline, in production.",
  },
  {
    tag: "Data + Reach",
    title: "Both. Not either-or.",
    body: "46M addressable contacts, audited to 96.1% verified data accuracy across UK & EMEA.",
  },
  {
    tag: "End-to-End SLAs",
    title: "One accountable partner.",
    body: "Awareness to BANT — end-to-end SLAs, no vendor stitching, no spreadsheet handovers.",
  },
  {
    tag: "First-Party Reach",
    title: "3.1M+ subscribers.",
    body: "One flagship publisher property — first-party UK & EMEA tech reach, not rented lists.",
  },
];

const REASONS = [
  {
    n: "01",
    title: "We understand B2B sales — we get it.",
    body: "Built by B2B sales practitioners, for B2B sales teams — every strategy grounded in how your buyers actually buy.",
    statNum: 90,
    statSuffix: "%",
    statDecimals: 0,
    statLabel: "of our team has 8+ years of B2B sales experience.",
  },
  {
    n: "02",
    title: "AI adoption across the delivery value chain.",
    body: "AI embedded at every stage — your campaigns get smarter, faster, and more efficient with every cycle, not just at launch.",
    statNum: 35,
    statPrefix: "+",
    statSuffix: "%",
    statDecimals: 0,
    statLabel: "improvement in campaign performance within the first 60 days.",
  },
  {
    n: "03",
    title: "Multi-level intent signal processing.",
    body: "We decode buying intent at every level of the funnel — your team spends time only on accounts that are actually ready.",
    statNum: 3,
    statSuffix: "\u00d7",
    statDecimals: 0,
    statLabel: "more accurate targeting via intent signals across 12+ buying stages.",
  },
  {
    n: "04",
    title: "Proprietary first-party intent data.",
    body: "Verified first-party intent across every buying persona — visibility into who's in-market before your competitors do.",
    statNum: 4.3,
    statSuffix: "M+",
    statDecimals: 1,
    statLabel: "verified first-party intent data points across 20+ job functions.",
  },
  {
    n: "05",
    title: "A lead scoring framework built for focus.",
    body: "A scoring model that tells your reps exactly who to call first — turning pipeline into a priority list, not a guessing game.",
    statNum: 4,
    statSuffix: "\u00d7",
    statDecimals: 0,
    statLabel: "faster conversion for prioritized leads vs. unscored pipeline.",
  },
  {
    n: "06",
    title: "One conversation, every channel.",
    body: "Coordinated engagement across paid, email, and tele — your buyers experience a consistent journey, not disconnected touchpoints.",
    statNum: 2.5,
    statSuffix: "\u00d7",
    statDecimals: 1,
    statLabel: "higher engagement from multi-channel vs. single-channel outreach.",
  },
];

export default function WhyUs() {
  return (
    <section
      id="why"
      data-testid="why-us-section"
      className="relative py-24 md:py-32 border-t border-white/5 overflow-hidden"
    >
      {/* Ambient gradient orb */}
      <div aria-hidden className="absolute -left-40 top-40 h-[420px] w-[420px] rounded-full bg-teal/[0.05] blur-3xl pointer-events-none why-orb" />

      <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
        <Reveal className="max-w-3xl">
          <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-teal mb-4">
            Why RevGrow · Four pillars, one accountable partner
          </div>
          <h2 className="font-display font-semibold text-4xl md:text-5xl lg:text-[64px] leading-[0.98] tracking-[-0.02em]">
            Demand gen built on signal,{" "}
            <span className="italic font-normal text-teal">not spray-and-pray.</span>
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => (
            <Reveal key={p.title} delay={i * 110} y={26}>
              <div className="rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 card-hover relative overflow-hidden h-full">
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-teal mb-6">
                  {p.tag}
                </div>
                <h3 className="font-display text-xl font-semibold mb-3 leading-tight">
                  {p.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.body}</p>
                <span className="absolute right-0 bottom-0 h-16 w-16 rounded-tl-2xl bg-gradient-to-tl from-teal/10 to-transparent pointer-events-none pillar-corner" style={{ animationDelay: `${i * 0.5}s` }} />
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 space-y-3">
          {REASONS.map((r, i) => (
            <Reveal key={r.n} delay={i * 90} y={20}>
              <div className="group rounded-2xl border border-white/[0.06] bg-[#0A121A] p-6 md:p-8 card-hover relative overflow-hidden">
                {/* left accent that fills on hover */}
                <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-teal/70 origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-500" />
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center">
                  <div className="md:col-span-1">
                    <div className="font-mono text-sm text-teal">{r.n}</div>
                  </div>
                  <div className="md:col-span-7">
                    <h3 className="font-display text-xl md:text-2xl font-semibold leading-tight">
                      {r.title}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{r.body}</p>
                  </div>
                  <div className="md:col-span-4 md:pl-8 md:border-l border-white/5">
                    <div className="font-display text-4xl md:text-5xl font-semibold text-teal">
                      <CountUp
                        end={r.statNum}
                        prefix={r.statPrefix || ""}
                        suffix={r.statSuffix}
                        decimals={r.statDecimals}
                      />
                    </div>
                    <div className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {r.statLabel}
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes floatOrb {
          0%, 100% { transform: translate(0, 0); }
          50% { transform: translate(30px, -20px); }
        }
        .why-orb { animation: floatOrb 14s ease-in-out infinite; }

        @keyframes cornerFade {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
        .pillar-corner { animation: cornerFade 5s ease-in-out infinite; }
      `}</style>
    </section>
  );
}
